import React, { useState, useEffect, useCallback } from 'react';

// In-app editor for team-leader / network-principal RCTI inputs.
// Persists overrides to /.netlify/functions/tl-rcti (Netlify Blob, keyed by period);
// the RCTI build reads them so "Build RCTIs from Sheets" produces the right lines.
// No Google Sheet editing required.

const TL_FN = '/.netlify/functions/tl-rcti';
const fmt = n => '$' + (Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const r4 = n => Math.round((Number(n) || 0) * 10000) / 10000;

const inp = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '.82rem', width: 90 };
const lbl = { fontSize: '.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', marginBottom: 3 };

export default function TeamLeaderRCTIEditor({ period }) {
  const endISO = period?.endISO;
  const periodLabel = period?.label || endISO;
  const [loading, setLoading] = useState(false);
  const [tls, setTls] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!endISO) return;
    setLoading(true); setMsg(null);
    try {
      const { fetchAllSheetData } = await import('../services/googleSheets');
      const data = await fetchAllSheetData(endISO);
      const nq = data.networkQuantities || {};
      const networkPrincipals = new Set(Object.keys(nq));
      const list = (data.teamLeaders || []).map(tl => {
        const login = tl.login.toUpperCase();
        const isNetwork = networkPrincipals.has(login);
        const isHourly = (tl.unitTypes || []).includes('Hourly');
        return {
          login, name: tl.name || login,
          descriptionOverride: tl.descriptionOverride || '',
          kind: isNetwork ? 'network' : (isHourly ? 'hourly' : 'perunit'),
          hoursPerDay: tl.hoursPerDay || 0,
          hourlyRateExGST: tl.hourlyRateIncGST ? r4(tl.hourlyRateIncGST / 1.1) : 0,
          parcels: isNetwork ? (nq[login]?.Parcel || 0) : 0,
          units: tl.units || 0,
        };
      }).sort((a, b) => a.login.localeCompare(b.login));
      setTls(list);
      const ovData = await (await fetch(`${TL_FN}?action=get&period=${encodeURIComponent(endISO)}`)).json();
      const ov = ovData.overrides || {};
      const seed = {};
      list.forEach(t => {
        const o = ov[t.login] || {};
        seed[t.login] = { label: o.label ?? '', loads: o.loads ?? '', perParcelRate: o.perParcelRate ?? '', extras: o.extras ? o.extras.map(e => ({ ...e })) : [] };
      });
      setEdits(seed);
    } catch (e) { setMsg('Could not load: ' + e.message); }
    finally { setLoading(false); }
  }, [endISO]);

  useEffect(() => { load(); }, [load]);

  const setField = (login, field, val) => setEdits(p => ({ ...p, [login]: { ...p[login], [field]: val } }));
  const setExtra = (login, i, field, val) => setEdits(p => { const ex = [...(p[login].extras || [])]; ex[i] = { ...ex[i], [field]: val }; return { ...p, [login]: { ...p[login], extras: ex } }; });
  const addExtra = (login) => setEdits(p => ({ ...p, [login]: { ...p[login], extras: [...(p[login].extras || []), { description: '', amount: '' }] } }));
  const removeExtra = (login, i) => setEdits(p => { const ex = [...(p[login].extras || [])]; ex.splice(i, 1); return { ...p, [login]: { ...p[login], extras: ex } }; });

  const save = async (t) => {
    setSaving(t.login); setMsg(null);
    try {
      const e = edits[t.login] || {};
      const override = {
        ...(e.label && e.label.trim() ? { label: e.label.trim() } : {}),
        ...(e.loads !== '' && e.loads != null ? { loads: Number(e.loads) } : {}),
        ...(e.perParcelRate !== '' && e.perParcelRate != null ? { perParcelRate: Number(e.perParcelRate) } : {}),
        extras: (e.extras || []).filter(x => x.description && x.amount).map(x => ({ description: x.description, amount: Number(x.amount) })),
      };
      const d = await (await fetch(`${TL_FN}?action=save`, { method: 'POST', body: JSON.stringify({ period: endISO, login: t.login, override }) })).json();
      setMsg(d.success ? `Saved ${t.login} — rebuild RCTIs to apply` : `Save failed: ${d.error}`);
    } catch (err) { setMsg('Save failed: ' + err.message); }
    finally { setSaving(null); }
  };

  // Live fee preview for the editable line
  const preview = (t) => {
    const e = edits[t.login] || {};
    if (t.kind === 'hourly') {
      const loads = e.loads === '' || e.loads == null ? 0 : Number(e.loads);
      return { label: `${e.label || t.descriptionOverride || 'Team Leader Fee'} — ${loads} loads`, calc: `${loads} × ${fmt(t.hourlyRateExGST)}`, amount: r4(loads * t.hourlyRateExGST) };
    }
    if (t.kind === 'network') {
      const rate = e.perParcelRate === '' || e.perParcelRate == null ? 0 : Number(e.perParcelRate);
      if (rate > 0) return { label: e.label || 'Parcel Delivery Coordination Fee', calc: `${t.parcels.toLocaleString()} parcels × ${fmt(rate)}`, amount: r4(t.parcels * rate) };
      return { label: 'Network Coordination Fee (flat — set a per-parcel rate to switch)', calc: `${t.units} units`, amount: null };
    }
    return { label: 'Per-unit (parcels/depot from scans)', calc: '', amount: null };
  };

  const card = { border: '1px solid var(--hp-border)', borderRadius: 10, padding: 14, background: 'var(--hp-surface)' };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Team Leader &amp; Network RCTI</h3>
        <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>{periodLabel}</span>
        {loading && <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>· loading…</span>}
      </div>
      <p style={{ fontSize: '.78rem', color: 'var(--hp-dim)', margin: '0 0 12px' }}>
        Set loads, per-parcel coordination rates and extra fees here instead of the Google Sheet. Save, then <strong>Build RCTIs from Sheets</strong> to apply.
      </p>
      {msg && <div style={{ fontSize: '.8rem', marginBottom: 10, color: msg.startsWith('Saved') ? 'var(--hp-green)' : 'var(--hp-gold)' }}>{msg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: 12 }}>
        {tls.map(t => {
          const e = edits[t.login] || { extras: [] };
          const pv = preview(t);
          return (
            <div key={t.login} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <div>
                  <strong style={{ fontSize: '.9rem' }}>{t.login}</strong>
                  <span style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginLeft: 6 }}>{t.name}</span>
                </div>
                <span style={{ fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)' }}>{t.kind}</span>
              </div>

              {(t.kind === 'hourly' || t.kind === 'network') && (
                <label style={{ display: 'block', marginBottom: 10 }}>
                  <span style={lbl}>Line label</span>
                  <input style={{ ...inp, width: '100%' }} value={e.label} placeholder={t.descriptionOverride || (t.kind === 'network' ? 'Parcel Delivery Coordination Fee' : 'Team Leader Fee')} onChange={ev => setField(t.login, 'label', ev.target.value)} />
                </label>
              )}

              {t.kind === 'hourly' && (
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', marginBottom: 10 }}>
                  <label><span style={lbl}>Loads</span>
                    <input type="number" min="0" style={inp} value={e.loads} placeholder="0" onChange={ev => setField(t.login, 'loads', ev.target.value)} />
                  </label>
                  <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}>@ {fmt(t.hourlyRateExGST)} ex per load</div>
                </div>
              )}

              {t.kind === 'network' && (
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', marginBottom: 10 }}>
                  <label><span style={lbl}>Per-parcel rate ($ ex)</span>
                    <input type="number" min="0" step="0.0001" style={inp} value={e.perParcelRate} placeholder="(flat)" onChange={ev => setField(t.login, 'perParcelRate', ev.target.value)} />
                  </label>
                  <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}>{t.parcels.toLocaleString()} parcels this period</div>
                </div>
              )}

              <div style={{ fontSize: '.8rem', padding: '7px 10px', background: 'var(--hp-bg)', borderRadius: 6, marginBottom: 10 }}>
                {pv.label}{pv.calc ? <> — <span style={{ color: 'var(--hp-dim)' }}>{pv.calc}</span></> : ''}
                {pv.amount != null && <strong style={{ float: 'right' }}>{fmt(pv.amount)}</strong>}
              </div>

              <div style={{ marginBottom: 8 }}>
                <span style={lbl}>Extra fees (e.g. Coordination Fee)</span>
                {(e.extras || []).map((x, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                    <input style={{ ...inp, width: 'auto', flex: 1 }} placeholder="Description" value={x.description} onChange={ev => setExtra(t.login, i, 'description', ev.target.value)} />
                    <input type="number" step="0.01" style={inp} placeholder="$ ex" value={x.amount} onChange={ev => setExtra(t.login, i, 'amount', ev.target.value)} />
                    <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px' }} onClick={() => removeExtra(t.login, i)}>✕</button>
                  </div>
                ))}
                <button className="btn btn-ghost btn-sm" style={{ fontSize: '.72rem' }} onClick={() => addExtra(t.login)}>+ Add fee</button>
              </div>

              <button className="btn btn-primary btn-sm" disabled={saving === t.login} onClick={() => save(t)} style={{ width: '100%' }}>
                {saving === t.login ? 'Saving…' : 'Save'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
